export type CaseType = '全部' | '房地产' | '债券' | '大宗商品' | '股权' | '碳信用' | '供应链信贷' | '其他';

export interface CaseItem {
  id: number;
  name: string;
  type: string;
  region: string;
  platform: string;
  tvl: string;
  status: string;
  track: string;
  desc: string;
  detail?: string;
  launchDate?: string;
  issuer?: string;
  standard?: string;
  contractAddress?: string;
  contractCode?: string;
  highlights?: string[];
  links?: { label: string; url: string }[];
}

export const CASE_TYPES: CaseType[] = ['全部', '房地产', '债券', '大宗商品', '股权', '碳信用', '供应链信贷', '其他'];

export const cases: CaseItem[] = [
  // ── 债券 ──────────────────────────────────────────────────────────────────
  {
    id: 1,
    name: 'Blackrock BUIDL — 短期国债代币化',
    type: '债券', region: '美国', platform: 'Ethereum', tvl: '$10亿+', status: '运营中', track: '机构级', launchDate: '2024.03', issuer: 'BlackRock / Securitize', standard: 'ERC-20 + Securitize合规层',
    desc: '贝莱德与Securitize合作发行BUIDL基金，持有美国政府证券，USDC即时赎回，规模已突破10亿美元，是全球规模最大的代币化国债产品。',
    detail: 'BUIDL（BlackRock USD Institutional Digital Liquidity Fund）由贝莱德与Securitize合作推出，持有美国政府证券、回购协议等短期资产，1 BUIDL = $1 + 每日计息。Circle提供USDC即时赎回通道，机构无需等待T+1，实现真正的链上流动性。',
    highlights: ['全球规模最大代币化货币市场基金', '与Circle合作实现USDC即时赎回', 'Ondo/Superstate等多家机构作为流动性合作伙伴', '每日链上分红自动执行'],
    contractAddress: '0x7712c34205737192402172409a8F7ccef8aA2AEc',
    links: [{ label: 'Securitize官网', url: 'https://securitize.io' }],
  },
  {
    id: 2,
    name: 'Ondo Finance OUSG — 美国国债代币化',
    type: '债券', region: '美国', platform: 'Ethereum', tvl: '$6.2亿', status: '运营中', track: '机构级', launchDate: '2023.01', issuer: 'Ondo Finance', standard: 'ERC-4626 + ERC-20',
    desc: '将美国短期国债代币化为OUSG，机构投资者可通过DeFi协议获得国债收益，Chainlink提供实时NAV数据。底层资产为iShares短期国债ETF。',
    detail: 'OUSG是Ondo Finance发行的代币化美国短期国债产品，底层持有BlackRock iShares 0-3月期国债ETF（SHV）。采用ERC-4626金库标准，份额代币自动反映利息累积。用户须完成Ondo的KYC并满足最低10万美元投资门槛。',
    highlights: ['底层持有iShares 0-3月国债ETF（SHV）', 'Chainlink预言机每日更新NAV', '与Flux Finance集成实现OUSG抵押借贷', 'rOUSG版本支持账户余额自动增值'],
    contractAddress: '0x1B19C19393e2d034D8Ff31ff34c81252FcBbe39',
    standard: 'ERC-4626 + ERC-20',
    contractCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.16;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/AccessControl.sol";
import "@chainlink/contracts/src/v0.8/interfaces/AggregatorV3Interface.sol";

/**
 * @title OUSG - Ondo Short-Term US Government Bond Fund
 * @notice ERC-20 token representing shares in the OUSG fund
 * @dev Integrates Chainlink oracle for daily NAV price feed
 */
contract OUSG is ERC20, AccessControl {
    bytes32 public constant MINTER_ROLE = keccak256("MINTER_ROLE");
    bytes32 public constant BURNER_ROLE = keccak256("BURNER_ROLE");
    bytes32 public constant KYC_CONFIGURER_ROLE = keccak256("KYC_CONFIGURER_ROLE");

    // Chainlink price feed for NAV
    AggregatorV3Interface internal navPriceFeed;
    
    // KYC registry: approved addresses
    mapping(address => bool) public kycApproved;
    
    // Minimum investment: $100,000 USDC
    uint256 public constant MIN_INVESTMENT = 100_000 * 1e6;
    
    event KYCStatusUpdated(address indexed account, bool approved);
    event NAVUpdated(uint256 newNAV, uint256 timestamp);

    constructor(address _priceFeed) ERC20("Ondo Short-Term US Government Bond", "OUSG") {
        navPriceFeed = AggregatorV3Interface(_priceFeed);
        _grantRole(DEFAULT_ADMIN_ROLE, msg.sender);
    }

    /**
     * @dev Returns current NAV per share from Chainlink oracle
     */
    function getLatestNAV() public view returns (int256 price, uint256 updatedAt) {
        (, price, , updatedAt,) = navPriceFeed.latestRoundData();
        require(block.timestamp - updatedAt < 86400, "NAV: stale price data");
    }

    /**
     * @dev Override transfer to enforce KYC whitelist
     */
    function _beforeTokenTransfer(
        address from,
        address to,
        uint256 amount
    ) internal override {
        // Allow minting/burning, enforce KYC for transfers
        if (from != address(0) && to != address(0)) {
            require(kycApproved[from], "OUSG: sender not KYC approved");
            require(kycApproved[to], "OUSG: recipient not KYC approved");
        }
        super._beforeTokenTransfer(from, to, amount);
    }

    /**
     * @dev Mint shares to KYC-approved investor
     */
    function mint(address to, uint256 amount) external onlyRole(MINTER_ROLE) {
        require(kycApproved[to], "OUSG: recipient not KYC approved");
        _mint(to, amount);
    }

    /**
     * @dev Redeem shares - burn tokens and initiate USDC transfer
     */
    function redeem(uint256 shares) external {
        require(kycApproved[msg.sender], "OUSG: not KYC approved");
        require(balanceOf(msg.sender) >= shares, "OUSG: insufficient balance");
        _burn(msg.sender, shares);
        // Actual USDC transfer handled by off-chain settlement layer
        emit Transfer(msg.sender, address(0), shares);
    }

    function setKYCStatus(address account, bool approved) 
        external onlyRole(KYC_CONFIGURER_ROLE) 
    {
        kycApproved[account] = approved;
        emit KYCStatusUpdated(account, approved);
    }
}`,
  },
  {
    id: 3,
    name: 'Franklin Templeton BENJI — 货币市场基金',
    type: '债券', region: '美国', platform: 'Stellar/Polygon', tvl: '$4.1亿', status: '运营中', track: '机构级', launchDate: '2021.04', issuer: 'Franklin Templeton', standard: 'Stellar原生代币 + Polygon ERC-20',
    desc: '富兰克林邓普顿将Benji货币市场基金份额上链，是全球首个在区块链登记的美国注册基金，首发于Stellar，后扩展至Polygon。',
    highlights: ['全球首个区块链登记的SEC注册美国基金', 'Stellar + Polygon双链部署', 'Benji App直接面向个人零售投资者', '每日NAV公链可查'],
  },
  {
    id: 4,
    name: 'HSBC Orion — 香港政府绿债',
    type: '债券', region: '香港', platform: 'Private Chain', tvl: 'HK$8亿', status: '已发行', track: '主权级', launchDate: '2023.02', issuer: 'HKSAR政府 / 汇丰承销', standard: 'ERC-1400 私有链',
    desc: '汇丰HSBC Orion平台承销香港政府2023年发行的全球首只数字化政府绿色债券，利用区块链实现T+0结算，较传统债券结算周期缩短2天。',
    highlights: ['全球首只数字化政府绿色债券', 'T+0原子结算，消除结算风险', '香港金融管理局背书', 'HSBC Orion平台首次商业化应用'],
  },
  {
    id: 5,
    name: 'Standard Chartered Libeara — 美债代币',
    type: '债券', region: '香港', platform: 'Ethereum', tvl: '$5亿', status: '运营中', track: '机构级', launchDate: '2023.11', issuer: '渣打银行 Libeara', standard: 'ERC-3643',
    desc: '渣打银行Libeara平台代币化美国国债，面向香港专业投资者，SFC合规框架下运营，是传统银行进军RWA的重要里程碑案例。',
    highlights: ['传统银行发行RWA的标杆案例', 'SFC合规框架下运营', 'ERC-3643 T-REX合规标准', '面向香港专业投资者'],
  },
  {
    id: 6,
    name: 'BOCI — 数字化结构性票据',
    type: '债券', region: '香港', platform: 'Ethereum', tvl: 'HK$2.28亿', status: '已发行', track: '首例', launchDate: '2023.07', issuer: '中银国际 BOCI', standard: 'ERC-1400',
    desc: '中银国际于2023年在以太坊上发行2.28亿港元数字化结构性票据，是中资背景机构在以太坊主网发行STO的全球首例，具有里程碑意义。',
    highlights: ['中资机构以太坊发行STO首例', '2.28亿港元规模', '瑞银承销支持', '香港监管合规框架下发行'],
  },
  {
    id: 7,
    name: 'Societe Generale — 欧洲结构化债券STO',
    type: '债券', region: '欧盟', platform: 'Ethereum / Tezos', tvl: '€1亿', status: '运营中', track: '机构级', launchDate: '2021.04', issuer: 'Societe Generale FORGE', standard: 'ERC-1400',
    desc: '法国兴业银行旗下SG FORGE发行欧洲首批机构级债券STO，底层持有结构性金融产品，在以太坊和Tezos双链发行，符合MiFID II监管框架。',
    highlights: ['欧洲首批机构级STO', 'MiFID II合规框架', '以太坊+Tezos双链发行', 'MakerDAO购买作为DAI抵押品'],
  },
  {
    id: 8,
    name: 'EIB — 欧洲投资银行数字债券',
    type: '债券', region: '欧盟', platform: 'Ethereum', tvl: '€1亿', status: '已发行', track: '主权级', launchDate: '2021.04', issuer: '欧洲投资银行 EIB', standard: '私有链+公链结合',
    desc: '欧洲投资银行与高盛、桑坦德银行合作，在以太坊公链发行全球首笔主权机构数字债券，2年期，息率0%，开创主权机构使用公链先例。',
    highlights: ['主权机构使用以太坊公链首例', '高盛、桑坦德联合承销', '2年期零息债券', '欧盟CBDC对接测试'],
  },
  {
    id: 9,
    name: 'Siemens — 德国企业数字债券',
    type: '债券', region: '欧盟', platform: 'Polygon', tvl: '€6000万', status: '已发行', track: '企业级', launchDate: '2023.02', issuer: '西门子股份公司', standard: 'ERC-20 + 合规层',
    desc: '西门子在Polygon公链上发行6000万欧元1年期数字债券，是德国《电子证券法》生效后首批企业数字债券之一，无需传统纸质证券凭证。',
    highlights: ['德国首批电子证券法框架下企业债券', '摒弃传统纸质证书', '单日完成发行（传统需数周）', '标普B+级评级'],
  },
  {
    id: 10,
    name: 'ING — 欧洲绿色债券代币化',
    type: '债券', region: '欧盟', platform: 'Private', tvl: '€3亿', status: '试点', track: '机构级', launchDate: '2023.10', issuer: 'ING银行', standard: '私有DLT',
    desc: 'ING银行在MiCA框架下试点数字化绿色债券，通过分布式账本完成结算，测试T+0即时清算效果，是欧洲银行MiCA合规探索的代表案例。',
    highlights: ['MiCA框架首批合规探索', 'T+0绿色债券结算测试', 'ESG合规评级内嵌合约', 'ABN AMRO联合参与'],
  },
  {
    id: 11,
    name: 'ABN AMRO — 荷兰绿色债券代币化',
    type: '债券', region: '欧盟', platform: 'Ethereum', tvl: '€5亿', status: '试点', track: '机构级', launchDate: '2023.11', issuer: 'ABN AMRO银行', standard: 'ERC-1400',
    desc: '荷兰ABN AMRO银行完成欧洲首批商业银行代币化绿色债券，借助以太坊实现了债券发行、利息支付、到期偿还全流程链上执行，开创商业银行DLT发债先河。',
    highlights: ['欧洲商业银行DLT发债先河', '全流程链上执行（发行/利息/赎回）', 'ESG报告链上存证', 'DTCC互操作性测试'],
  },
  {
    id: 12,
    name: 'Superstate — 短期美债基金代币',
    type: '债券', region: '美国', platform: 'Ethereum', tvl: '$2.5亿', status: '运营中', track: '机构级', launchDate: '2023.08', issuer: 'Superstate Inc.', standard: 'ERC-20 + 白名单',
    desc: 'Compound创始人Robert Leshner创办的RWA项目，发行USTB代币代表美国短期国债基金份额，专注于机构DeFi场景的原生整合。',
    highlights: ['Compound创始人创办', 'DeFi原生深度整合', 'Ondo BlackRock BUIDL竞争对标', '机构客户专属白名单KYC'],
  },
  {
    id: 13,
    name: 'Mountain Protocol USDM — 美债收益稳定币',
    type: '债券', region: '百慕大', platform: 'Ethereum', tvl: '$2.1亿', status: '运营中', track: '合规级', launchDate: '2023.09', issuer: 'Mountain Protocol', standard: 'ERC-20 rebase',
    desc: '将美国国债收益以rebase机制分配给稳定币持有者，持有USDM钱包余额每日自动增加，背后持有美债，获百慕大FSC监管牌照，是货币市场基金代币化的新形态。',
    highlights: ['rebase机制：余额每日自动增加', '百慕大FSC牌照', '非美国居民可使用', 'DeFi生态深度整合（Curve、Aave等）'],
  },
  {
    id: 14,
    name: 'Maple Finance — 机构无抵押信贷代币化',
    type: '债券', region: '美国/澳大利亚', platform: 'Ethereum/Solana', tvl: '$3.5亿', status: '运营中', track: 'DeFi', launchDate: '2021.05', issuer: 'Maple Finance', standard: 'ERC-20 Pool Token',
    desc: 'Maple Finance将机构无抵押信贷池代币化，加密机构（做市商、矿企等）可从链上借款池借贷，信贷评估通过链下KYB+链上信用评分完成，是DeFi信贷RWA最大平台之一。',
    highlights: ['机构无抵押信贷RWA先驱', '链下信用评估+链上执行', '经历2022年熊市压力测试（部分违约）后全面改革', '重建后推出现金管理池'],
  },
  {
    id: 15,
    name: 'Clearpool Finance — 无抵押机构信贷池',
    type: '债券', region: '全球', platform: 'Ethereum/Polygon', tvl: '$5亿', status: '运营中', track: 'DeFi', launchDate: '2022.03', issuer: 'Clearpool Finance', standard: 'ERC-20 cpToken',
    desc: '机构借款人在链上创建信贷池，零售LP提供资金获取利息，已有Jane Street、Wintermute等头部做市商参与，是无抵押链上机构借贷的主要平台。',
    highlights: ['Jane Street、Wintermute等机构参与', '公开资金池竞争利率机制', 'cpToken即时流通性', '审计评分公开透明'],
  },
  {
    id: 16,
    name: 'Centrifuge Tinlake — 供应链应收账款RWA',
    type: '供应链信贷', region: '全球', platform: 'Centrifuge Chain', tvl: '$3.5亿', status: '运营中', track: 'DeFi', launchDate: '2020.08', issuer: 'Centrifuge', standard: 'ERC-721 NFT + ERC-20 Pool',
    desc: '将中小企业应收账款、房产贷款、汽车贷款等代币化为NFT后接入MakerDAO等DeFi借贷协议获取流动性，开创资产+DeFi融合发展模式。',
    highlights: ['首创"资产NFT化+DeFi"模式', 'MakerDAO核心RWA供应商', 'DROP/TIN双层结构（优先/劣后）', '全球中小企业融资服务'],
    contractCode: `// SPDX-License-Identifier: AGPL-3.0-only
pragma solidity ^0.7.6;

import {ERC20} from "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import {SafeMath} from "@openzeppelin/contracts/math/SafeMath.sol";

/**
 * @title Tinlake DROP Token (Senior Tranche)
 * @notice Represents senior pool participation - lower risk, stable return
 * @dev DROP/TIN structure provides risk tranching for RWA pool
 */
contract DropToken is ERC20 {
    using SafeMath for uint256;

    address public immutable pool;
    
    // Current DROP token price (in RAY = 10^27)
    uint256 public tokenPrice;
    
    // Senior pool parameters
    uint256 public seniorInterestRate; // per second interest rate in RAY
    uint256 public lastUpdated;

    event PriceUpdated(uint256 newPrice, uint256 timestamp);
    event DroppedIntoPool(address indexed investor, uint256 dai, uint256 dropMinted);
    event RedeemedFromPool(address indexed investor, uint256 drop, uint256 daiReceived);

    modifier onlyPool() {
        require(msg.sender == pool, "DROP: only pool can call");
        _;
    }

    constructor(address _pool) ERC20("Tinlake DROP Token", "DROP") {
        pool = _pool;
        tokenPrice = 10**27; // Initial price: 1 RAY
        lastUpdated = block.timestamp;
    }

    /**
     * @dev Calculate current DROP price with accumulated interest
     * Interest accrues continuously based on senior interest rate
     */
    function currentPrice() public view returns (uint256) {
        uint256 timePassed = block.timestamp.sub(lastUpdated);
        // price = tokenPrice * (1 + rate)^time (simplified for gas)
        return tokenPrice.add(
            tokenPrice.mul(seniorInterestRate).mul(timePassed).div(10**27).div(365 days)
        );
    }

    /**
     * @dev Investor supplies DAI, receives DROP tokens
     * Amount of DROP = DAI supplied / current DROP price
     */
    function supply(address investor, uint256 daiAmount) external onlyPool returns (uint256 dropAmount) {
        dropAmount = daiAmount.mul(10**27).div(currentPrice());
        _mint(investor, dropAmount);
        emit DroppedIntoPool(investor, daiAmount, dropAmount);
    }

    /**
     * @dev Investor redeems DROP tokens for DAI
     * DAI received = DROP amount * current DROP price
     */
    function redeem(address investor, uint256 dropAmount) external onlyPool returns (uint256 daiAmount) {
        require(balanceOf(investor) >= dropAmount, "DROP: insufficient balance");
        daiAmount = dropAmount.mul(currentPrice()).div(10**27);
        _burn(investor, dropAmount);
        emit RedeemedFromPool(investor, dropAmount, daiAmount);
    }

    /**
     * @dev Update price - called by pool on each NAV update
     */
    function updatePrice(uint256 newPrice) external onlyPool {
        tokenPrice = newPrice;
        lastUpdated = block.timestamp;
        emit PriceUpdated(newPrice, block.timestamp);
    }
}`,
  },
  {
    id: 17,
    name: 'Goldfinch — 新兴市场信贷RWA',
    type: '供应链信贷', region: '全球', platform: 'Ethereum', tvl: '$1.0亿', status: '运营中', track: 'DeFi', launchDate: '2021.01', issuer: 'Goldfinch Protocol', standard: 'ERC-20 FIDU + NFT借款凭证',
    desc: '链上信贷协议将非洲、东南亚、拉美新兴市场企业贷款代币化，加密原生投资者通过DeFi获得高收益，解决新兴市场实体企业的融资难题。',
    highlights: ['覆盖肯尼亚、印度、墨西哥等新兴市场', '无需加密抵押的链上信贷', '双层结构：高级池+初级池（Backers）', '已服务超500家新兴市场企业'],
  },
  // ── 房地产 ──────────────────────────────────────────────
  {
    id: 18,
    name: 'RealT — 美国住宅地产代币化',
    type: '房地产', region: '美国', platform: 'Ethereum/Gnosis', tvl: '$1.0亿+', status: '运营中', track: '零售级', launchDate: '2019.06', issuer: 'RealT LLC', standard: 'ERC-20 (每房产独立合约)',
    desc: '底特律等城市住宅房产代币化，代币持有者每日按比例获得租金收入，最低50美元起投，是全球最早且规模最大的住宅地产代币化项目。',
    highlights: ['全球最早住宅地产代币化项目', '最低$50起投门槛', '每周链上自动分红租金', '已代币化900+套美国住宅'],
    contractCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/Ownable.sol";

/**
 * @title RealT Property Token
 * @notice Each deployed contract represents ONE specific property
 * @dev Token holders receive proportional rental income distributions
 * 
 * Property: 9943 Marlowe St, Detroit, MI 48227
 * Total Tokens: 100,000 (= 100% ownership)
 * Monthly Rent: $2,500 USD distributed proportionally
 */
contract RealTPropertyToken is ERC20, Ownable {
    
    // Property metadata stored on-chain
    string public propertyAddress;
    string public propertyCity;
    uint256 public totalPropertyTokens;
    uint256 public monthlyRentUSD; // in cents
    
    // Distribution tracking
    uint256 public totalRentDistributed;
    mapping(address => uint256) public pendingRent;
    mapping(address => uint256) private _lastSnapshotBalance;
    
    // Whitelist - only KYC-verified holders
    mapping(address => bool) public whitelisted;
    
    event RentDistributed(uint256 amount, uint256 timestamp);
    event RentClaimed(address indexed holder, uint256 amount);
    event HolderWhitelisted(address indexed holder);

    constructor(
        string memory _propertyAddress,
        string memory _city,
        uint256 _totalTokens,
        uint256 _monthlyRentCents
    ) ERC20("RealT Property Token", "REALT") Ownable(msg.sender) {
        propertyAddress = _propertyAddress;
        propertyCity = _city;
        totalPropertyTokens = _totalTokens;
        monthlyRentUSD = _monthlyRentCents;
        _mint(msg.sender, _totalTokens);
    }

    /**
     * @dev Distribute rental income to all token holders proportionally
     * Called weekly with xDAI (Gnosis Chain stablecoin)
     */
    function distributeRent() external payable onlyOwner {
        require(msg.value > 0, "No rent to distribute");
        uint256 totalSupplyTokens = totalSupply();
        
        // Calculate rent per token (in wei)
        uint256 rentPerToken = msg.value / totalSupplyTokens;
        totalRentDistributed += msg.value;
        
        emit RentDistributed(msg.value, block.timestamp);
        // Individual claims tracked off-chain for gas efficiency
        // Holders call claimRent() to receive their share
    }

    /**
     * @dev Token holder claims their accumulated rental income
     */
    function claimRent() external {
        uint256 balance = balanceOf(msg.sender);
        require(balance > 0, "No tokens held");
        uint256 share = (totalRentDistributed * balance) / totalSupply();
        uint256 claimable = share - pendingRent[msg.sender];
        require(claimable > 0, "Nothing to claim");
        
        pendingRent[msg.sender] = share;
        payable(msg.sender).transfer(claimable);
        emit RentClaimed(msg.sender, claimable);
    }

    /**
     * @dev Enforce KYC whitelist on all transfers
     */
    function _update(address from, address to, uint256 value) internal override {
        if (from != address(0) && to != address(0)) {
            require(whitelisted[from] && whitelisted[to], "RealT: not whitelisted");
        }
        super._update(from, to, value);
    }

    function addToWhitelist(address holder) external onlyOwner {
        whitelisted[holder] = true;
        emit HolderWhitelisted(holder);
    }
}`,
  },
  {
    id: 19,
    name: 'Lofty AI — 美国租赁房产代币化',
    type: '房地产', region: '美国', platform: 'Algorand', tvl: '$5000万', status: '运营中', track: '零售级', launchDate: '2021.03', issuer: 'Lofty AI', standard: 'Algorand ASA',
    desc: '基于Algorand的美国租赁房产代币化平台，最低50美元起投，每日自动分配租金收益，并允许代币持有人通过DAO治理决定物业管理事项。',
    highlights: ['Algorand链低Gas费优势', '每日自动租金分配', 'DAO治理权（投票决定物业管理）', '已上线300+美国租赁房产'],
  },
  {
    id: 20,
    name: 'RedSwan CRE — 商业地产代币化',
    type: '房地产', region: '美国', platform: 'Ethereum', tvl: '$2.2亿', status: '运营中', track: '机构级', launchDate: '2020.01', issuer: 'RedSwan CRE', standard: 'ERC-1400',
    desc: '专注美国商业地产（写字楼、购物中心、酒店）代币化，面向机构级合格投资者，采用Reg D 506(c)豁免路径，资产价值普遍在1亿美元以上。',
    highlights: ['专注亿元级商业地产', 'Reg D 506(c)合规路径', '机构投资者专属（$50,000起投）', '包括休斯顿、达拉斯核心资产'],
  },
  {
    id: 21,
    name: 'ADDX — 新加坡REITs代币化',
    type: '房地产', region: '新加坡', platform: 'Private Chain', tvl: 'S$1.5亿', status: '运营中', track: '机构级', launchDate: '2021.06', issuer: 'ADDX (iSTOX)', standard: '私有许可链',
    desc: '新加坡MAS持牌平台，将私募REITs和私募基金代币化，最低投资门槛从百万美元降至1000新元，面向新加坡认可投资者。',
    highlights: ['MAS认可市场运营商牌照', 'REITs/PE/债券多类资产', '门槛从$100万降至$1000新元', '汉德尔斯班克等机构支持'],
  },
  {
    id: 22,
    name: 'Harbor — 美国私募REITs代币化',
    type: '房地产', region: '美国', platform: 'Ethereum', tvl: '$1亿', status: '运营中', track: '合规级', launchDate: '2018.10', issuer: 'Harbor', standard: 'ERC-1404 R-Token',
    desc: 'Harbor是美国最早的证券型代币合规平台之一，发行符合Reg D/S要求的私募REITs代币，其R-Token协议是ERC-1404的前驱实现，服务多个大型私募房地产基金。',
    highlights: ['美国证券型代币先驱平台', 'R-Token合规协议（ERC-1404前驱）', '科罗拉多州学生宿舍REITs首例', '已服务多个1亿美元以上REITs'],
  },
  {
    id: 23,
    name: 'Mapletree — 新加坡物流仓储RWA',
    type: '房地产', region: '新加坡', platform: 'Private Chain', tvl: 'S$1.2亿', status: '试点', track: '机构级', launchDate: '2022.11', issuer: 'Mapletree Investments / DBS', standard: '许可链',
    desc: '新加坡Mapletree集团试点将物流地产份额代币化，通过DBS银行合规渠道面向机构投资者，是新加坡大型房地产集团的RWA先行试验。',
    highlights: ['新加坡物流地产龙头试水RWA', 'DBS银行合规渠道分销', '物流仓储+产业园区资产组合', 'Project Guardian MAS沙盒参与'],
  },
  {
    id: 24,
    name: 'Brickken — 欧洲住宅地产代币化',
    type: '房地产', region: '欧盟', platform: 'Ethereum/Polygon', tvl: '€8000万', status: '运营中', track: '零售级', launchDate: '2021.09', issuer: 'Brickken SL', standard: 'ERC-1400',
    desc: '西班牙巴塞罗那为基地的欧洲住宅地产代币化平台，符合MiCA框架，最低100欧元起投，将西班牙、葡萄牙、法国住宅公寓代币化。',
    highlights: ['MiCA框架合规', '最低€100起投门槛', '西班牙/葡萄牙/法国黄金地段资产', '欧盟专业投资者 + 散户双通道'],
  },
  {
    id: 25,
    name: 'tZERO — 美国科技园区地产STO',
    type: '房地产', region: '美国', platform: 'tZERO Private', tvl: '$3000万', status: '运营中', track: '合规级', launchDate: '2021.04', issuer: 'tZERO', standard: '私有STO平台',
    desc: 'Overstock旗下tZERO发行美国科技园区地产STO，在其持牌ATS（另类交易系统）上提供代币化证券二级市场交易，是美国最早持牌ATS证券代币交易案例之一。',
    highlights: ['SEC注册持牌ATS二级市场', 'Overstock母公司背景', '美国首批证券代币ATS交易案例', '7×24小时可交易（不受传统市场时间限制）'],
  },
  // ── 大宗商品 ──────────────────────────────────────────────
  {
    id: 26,
    name: 'Paxos Gold PAXG — 实物黄金代币',
    type: '大宗商品', region: '美国', platform: 'Ethereum', tvl: '$6.8亿', status: '运营中', track: '零售级', launchDate: '2019.09', issuer: 'Paxos Trust Company', standard: 'ERC-20',
    desc: '每枚PAXG代表一盎司存于专业金库中的实物黄金，受美国NYDFS监管，可随时赎回实物黄金（最低430盎司），是全球规模最大的黄金代币。',
    highlights: ['NYDFS持牌，合规性最高', '1 PAXG = 1盎司伦敦金库实物黄金', 'Brinks/博依斯金库保管', '可随时赎回实物（最低430盎司）'],
    contractCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/Ownable.sol";

/**
 * @title PAX Gold (PAXG)
 * @notice Each token is backed by one fine troy ounce of gold
 * @dev NYDFS regulated, redeemable for physical gold (min 430 oz)
 * 
 * Real contract: 0x45804880De22913dAFE09f4980848ECE6EcbAf78
 */
contract PAXGold is ERC20, Ownable {
    
    // Storage fee: 0.15% per year (charged in PAXG)
    uint256 public constant ANNUAL_STORAGE_FEE_BPS = 15; // 0.15%
    
    // Transaction fee: 0.02% per transfer (min $0)
    uint256 public constant TX_FEE_BPS = 2; // 0.02%
    
    // Paxos fee collector address
    address public feeCollector;
    
    // NYDFS regulated: all holders must be verified
    mapping(address => bool) public sanctionedAddresses;
    
    // Gold bar serial numbers (stored off-chain, hash stored on-chain)
    bytes32 public goldBarInventoryHash;
    
    event GoldInventoryUpdated(bytes32 newHash, uint256 timestamp);
    event StorageFeePaid(address indexed holder, uint256 feeAmount);
    event AddressFrozen(address indexed account, string reason);

    constructor(address _feeCollector) ERC20("PAX Gold", "PAXG") Ownable(msg.sender) {
        feeCollector = _feeCollector;
    }

    /**
     * @dev Override transfer with fee deduction and sanctions check
     */
    function _update(address from, address to, uint256 value) internal override {
        require(!sanctionedAddresses[from], "PAXG: sender sanctioned");
        require(!sanctionedAddresses[to], "PAXG: recipient sanctioned");
        
        if (from != address(0) && to != address(0)) {
            // Calculate and deduct 0.02% transaction fee
            uint256 fee = (value * TX_FEE_BPS) / 10000;
            if (fee > 0) {
                super._update(from, feeCollector, fee);
                value -= fee;
            }
        }
        super._update(from, to, value);
    }

    /**
     * @dev Mint PAXG when new gold bars are added to vault
     * 1 PAXG = 1 troy ounce of .9999 fine gold
     */
    function mint(address to, uint256 ounces) external onlyOwner {
        _mint(to, ounces);
    }

    /**
     * @dev Burn PAXG when investor redeems for physical gold (min 430 oz)
     */
    function redeem(uint256 ounces) external {
        require(ounces >= 430, "PAXG: minimum redemption 430 oz");
        require(balanceOf(msg.sender) >= ounces, "PAXG: insufficient balance");
        _burn(msg.sender, ounces);
        // Physical delivery coordinated off-chain with Paxos team
    }

    /**
     * @dev Update gold bar inventory hash (proof of reserves)
     */
    function updateGoldInventoryHash(bytes32 newHash) external onlyOwner {
        goldBarInventoryHash = newHash;
        emit GoldInventoryUpdated(newHash, block.timestamp);
    }

    /**
     * @dev NYDFS compliance: freeze sanctioned addresses
     */
    function freezeAddress(address account, string calldata reason) external onlyOwner {
        sanctionedAddresses[account] = true;
        emit AddressFrozen(account, reason);
    }
}`,
  },
  {
    id: 27,
    name: 'Tether Gold XAUt — 黄金稳定代币',
    type: '大宗商品', region: '英属维京群岛', platform: 'Ethereum/Tron', tvl: '$5.2亿', status: '运营中', track: '零售级', launchDate: '2020.01', issuer: 'TG Commodities Limited', standard: 'ERC-20 + TRC-20',
    desc: 'Tether旗下黄金代币产品，1 XAUt代表1盎司伦敦好交割黄金，在瑞士保险库中实物保存，支持以太坊和波场链双链发行，是PAXG的主要竞争产品。',
    highlights: ['Tether品牌背书，流通性强', '瑞士金库实物保管', '以太坊+Tron双链发行', '可赎回标准400盎司金条'],
  },
  {
    id: 28,
    name: 'HSBC Gold Token — 香港实物黄金代币',
    type: '大宗商品', region: '香港', platform: 'Private', tvl: 'HK$2亿', status: '运营中', track: '零售级', launchDate: '2023.10', issuer: '汇丰香港 HSBC HK', standard: '私有链 ERC-20衍生',
    desc: '汇丰香港发行实物黄金支撑代币，面向零售合格投资者，可通过汇丰App直接购买，SFC监管合规，是传统银行将黄金理财产品代币化的里程碑案例。',
    highlights: ['传统银行零售端黄金代币化首例', '汇丰App直接购买，用户体验友好', 'SFC监管合规', '伦敦金属交易所认可黄金'],
  },
  {
    id: 29,
    name: 'Perth Mint Gold Token PMGT — 澳洲国家铸币局',
    type: '大宗商品', region: '澳大利亚', platform: 'Ethereum', tvl: '$2000万', status: '运营中', track: '合规级', launchDate: '2019.11', issuer: '澳大利亚珀斯铸币局', standard: 'ERC-20',
    desc: '由澳大利亚政府拥有的珀斯铸币局发行的黄金代币，每枚PMGT代表1/100盎司黄金，具有澳大利亚政府主权信用背书，是全球唯一政府铸币局发行的黄金代币。',
    highlights: ['澳大利亚政府主权信用背书', '全球唯一政府铸币局黄金代币', '最低$50澳元起购', '可赎回PMGT认证实物金条'],
  },
  {
    id: 30,
    name: 'Cache Gold CGT — 机构黄金代币',
    type: '大宗商品', region: '新加坡', platform: 'Ethereum', tvl: '$8000万', status: '运营中', track: '机构级', launchDate: '2019.07', issuer: 'Cache Pte Ltd', standard: 'ERC-20',
    desc: '面向机构投资者的黄金代币，1 CGT = 0.001克纯度99.99%金条，在全球多个认证金库分散保管，提供链上实时金库审计，独特的多地点保管策略降低集中风险。',
    highlights: ['全球多金库分散保管（降低集中风险）', '链上实时金库审计', '0.001克超小面值设计', '面向机构投资者和央行级客户'],
  },
  {
    id: 31,
    name: 'Comtech Gold CXG — 迪拜黄金代币',
    type: '大宗商品', region: '迪拜/中东', platform: 'Ethereum', tvl: '$1.5亿', status: '运营中', track: '合规级', launchDate: '2021.03', issuer: 'Comtech Gold', standard: 'ERC-20',
    desc: '迪拜国际金融中心（DIFC）框架下发行的黄金代币，1 CXG = 1克黄金，在迪拜DMCC黄金交易中心保管，是中东地区规模最大的黄金代币化项目，服务穆斯林投资者合规需求（符合伊斯兰金融原则）。',
    highlights: ['DIFC合规框架', 'DMCC黄金交易中心保管', '符合伊斯兰金融原则', '服务中东/中亚黄金投资者'],
  },
  {
    id: 32,
    name: 'AgriDigital — 澳洲粮食大宗商品代币化',
    type: '大宗商品', region: '澳大利亚', platform: 'Ethereum', tvl: 'A$3亿', status: '运营中', track: '农业级', launchDate: '2016.11', issuer: 'AgriDigital', standard: 'ERC-20',
    desc: '全球首个将粮食（小麦、大麦）大宗商品交割代币化的平台，粮食库存作为代币化资产存入仓库后即可在平台上实现即时结算，消除了传统农产品贸易中的结算延迟和欺诈风险。',
    highlights: ['全球粮食大宗商品代币化先驱（2016年）', '澳大利亚小麦/大麦交割链上化', '消除农产品贸易欺诈风险', '已处理超$3亿澳元粮食交易'],
  },
  // ── 股权 ──────────────────────────────────────────────────────────────────
  {
    id: 33,
    name: 'Securitize — 私募基金份额代币化',
    type: '股权', region: '美国', platform: 'Ethereum/Polygon', tvl: '$10亿+', status: '运营中', track: '机构级', launchDate: '2018.07', issuer: 'Securitize Inc.', standard: 'ERC-1400 DS Protocol',
    desc: 'Securitize作为SEC注册转让代理，代币化多个头部私募基金份额，包括KKR、Hamilton Lane、贝莱德等，是全球最大的证券型代币合规基础设施平台。',
    highlights: ['SEC注册转让代理（Transfer Agent）', 'KKR/Hamilton Lane/BlackRock合作', 'DS Protocol是ERC-1400的商业实现', '已发行40+证券型代币产品'],
  },
  {
    id: 34,
    name: 'Backed Finance — 欧洲股票代币化',
    type: '股权', region: '欧盟', platform: 'Ethereum/Polygon', tvl: '$8000万', status: '运营中', track: '合规级', launchDate: '2022.01', issuer: 'Backed Finance AG', standard: 'ERC-20 + 合规层',
    desc: '瑞士Backed Finance将苹果、TSLA、MSFT等美股代币化为xAAPL/xTSLA等代币，通过EEA合规监管框架发行，代币持有人间接持有真实股票，可在DeFi中使用。',
    highlights: ['美股代币化（苹果/特斯拉/微软等）', '欧洲BaFin合规框架', '代币可在DeFi中作为抵押品', 'DeFi股票敞口的合规解决方案'],
  },
  {
    id: 35,
    name: 'Dinari dShares — 美股链上化',
    type: '股权', region: '美国', platform: 'Ethereum/Arbitrum', tvl: '$5000万', status: '运营中', track: '合规级', launchDate: '2023.06', issuer: 'Dinari Inc.', standard: 'ERC-20 dShare',
    desc: 'Dinari发行美股代币（dShares），每枚代币1:1对应真实美股股票，在Arbitrum上低Gas发行，面向全球合规用户，支持碎片化持股（最低1美分）。',
    highlights: ['最低$0.01碎片化持股', 'Arbitrum L2低Gas费', '7×24小时全球交易（不限美股交易时间）', 'SEC豁免注册合规路径'],
  },
  {
    id: 36,
    name: 'ADDX — 私募股权基金代币化',
    type: '股权', region: '新加坡', platform: 'Private Chain', tvl: 'S$3亿', status: '运营中', track: '机构级', launchDate: '2020.09', issuer: 'ADDX Pte Ltd', standard: '许可链',
    desc: 'MAS持牌平台，将黑石、KKR等全球顶级私募股权基金份额代币化，将投资门槛从100万美元降至1000新元，是新加坡私募市场民主化的核心平台。',
    highlights: ['黑石/KKR等顶级PE代币化', 'MAS认可市场运营商牌照', '投资门槛降至$1000新元', '已引入$30亿+资产上平台'],
  },
  {
    id: 37,
    name: 'INX Digital — SEC注册证券代币IPO',
    type: '股权', region: '美国', platform: 'Ethereum', tvl: '$8500万', status: '运营中', track: '监管里程碑', launchDate: '2021.09', issuer: 'INX Limited', standard: 'ERC-20 + SEC注册',
    desc: 'INX完成全球首个向散户公开发行的SEC完整注册证券型代币IPO，筹资8500万美元，是加密资产监管史上的重要里程碑——证明了普通散户可以合法通过区块链参与证券发行。',
    highlights: ['全球首个SEC完整注册向散户发行的证券代币', '无需Reg D豁免，面向所有美国居民', '筹资$8500万', 'INX证券代币交易所同步上线'],
  },
  {
    id: 38,
    name: 'Polymesh — 证券型代币专属公链',
    type: '股权', region: '全球', platform: 'Polymesh Chain', tvl: '多项目累计$15亿', status: '运营中', track: '基础设施', launchDate: '2021.10', issuer: 'Polymath Inc.', standard: 'Polymesh原生标准',
    desc: 'Polymath专为证券型代币打造的合规公链，内置身份（DID）、合规（Compliance）、资产（Asset）三层原生设计，所有资产创建时即内置KYC/AML规则，无需额外智能合约实现合规。',
    highlights: ['专为证券型代币设计的合规公链', 'KYC/AML内置于共识层', '多家传统券商和基金公司已部署', 'MERJ证券交易所（塞舌尔）基础设施'],
  },
  // ── 碳信用 ──────────────────────────────────────────────
  {
    id: 39,
    name: 'Toucan Protocol BCT — 碳信用代币化基础层',
    type: '碳信用', region: '全球', platform: 'Polygon', tvl: '1500万吨', status: '运营中', track: 'DeFi', launchDate: '2021.10', issuer: 'Toucan Protocol', standard: 'ERC-20 BCT + ERC-721 TCO2',
    desc: '将Verra VCS注册的碳信用上链为TCO2（每个项目独立NFT），再汇聚为BCT（Base Carbon Tonne）同质化代币，接入Klima DAO等DeFi协议，是链上碳市场流动性的核心基础设施。',
    highlights: ['Verra VCS碳信用上链标准制定者', 'TCO2 NFT + BCT同质化双层架构', 'Klima DAO生态系统核心流动性来源', '已桥接2500万吨碳信用上链'],
    contractCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.0;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/token/ERC721/IERC721.sol";

/**
 * @title Base Carbon Tonne (BCT)
 * @notice Pool token aggregating multiple Verra VCS carbon credits
 * @dev TCO2 (ERC-721 individual project credits) are deposited into pool
 *      In exchange, depositor receives equivalent BCT (ERC-20) tokens
 * 
 * Architecture:
 * Verra Registry → Bridge → TCO2 (ERC-721) → BCT Pool → BCT (ERC-20)
 */
contract BaseCarbonTonne is ERC20 {
    
    // Accepted TCO2 token contracts (Verra VCS projects)
    mapping(address => bool) public acceptedTCO2s;
    
    // Retirement tracking: prevent double-counting
    mapping(bytes32 => bool) public retiredCredits;
    
    // Minimum vintage year for accepted credits (quality filter)
    uint256 public minimumVintageYear = 2008;
    
    // Total tonnes retired for carbon offsetting
    uint256 public totalRetired;
    
    event CarbonDeposited(address indexed tco2, uint256 tokenId, uint256 bctMinted);
    event CarbonRetired(address indexed beneficiary, uint256 amount, string reason);
    event TCO2Whitelisted(address indexed tco2, bool accepted);

    constructor() ERC20("Base Carbon Tonne", "BCT") {}

    /**
     * @dev Deposit TCO2 (individual carbon credit NFT) into pool
     * Returns equivalent BCT tokens (1 BCT = 1 tonne CO2)
     * TCO2 must represent verified Verra VCS credits
     */
    function deposit(address tco2Contract, uint256 tokenId) external returns (uint256 bctAmount) {
        require(acceptedTCO2s[tco2Contract], "BCT: TCO2 not accepted");
        
        // Transfer TCO2 NFT to pool (locking it)
        IERC721(tco2Contract).transferFrom(msg.sender, address(this), tokenId);
        
        // Each TCO2 token represents 1 tonne CO2 = 1 BCT
        bctAmount = 1e18; // 1 BCT in 18 decimal representation
        _mint(msg.sender, bctAmount);
        
        emit CarbonDeposited(tco2Contract, tokenId, bctAmount);
    }

    /**
     * @dev Retire BCT for carbon offsetting (irreversible - credits are burned)
     * Creates permanent on-chain retirement record
     */
    function retire(uint256 amount, string calldata beneficiaryName, string calldata retirementMessage) external {
        require(balanceOf(msg.sender) >= amount, "BCT: insufficient balance");
        
        _burn(msg.sender, amount);
        totalRetired += amount;
        
        // Create unique retirement certificate hash
        bytes32 retirementId = keccak256(
            abi.encodePacked(msg.sender, amount, block.timestamp, retirementMessage)
        );
        retiredCredits[retirementId] = true;
        
        emit CarbonRetired(msg.sender, amount, retirementMessage);
        // NFT retirement certificate minted off-chain via Toucan API
    }

    /**
     * @dev Redeem BCT for specific TCO2 tokens (selective redemption)
     * Burns BCT and returns the specific project carbon credit
     */
    function redeemAuto(uint256 bctAmount) external returns (address[] memory tco2s, uint256[] memory amounts) {
        require(balanceOf(msg.sender) >= bctAmount, "BCT: insufficient balance");
        _burn(msg.sender, bctAmount);
        // Actual TCO2 NFT return handled by pool manager (gas optimization)
    }

    function whitelistTCO2(address tco2, bool accepted) external {
        // Governance-controlled - Toucan DAO vote required
        acceptedTCO2s[tco2] = accepted;
        emit TCO2Whitelisted(tco2, accepted);
    }
}`,
  },
  {
    id: 40,
    name: 'KlimaDAO KLIMA — 碳储备货币协议',
    type: '碳信用', region: '全球', platform: 'Polygon', tvl: '1700万吨碳储备', status: '运营中', track: 'DeFi', launchDate: '2021.10', issuer: 'KlimaDAO', standard: 'ERC-20 KLIMA',
    desc: 'KlimaDAO通过购买大量链上碳信用（BCT等）作为储备资产，发行KLIMA代币，旨在通过DeFi机制推高碳信用价格，激励更多碳减排项目，是最大的链上碳经济实验。',
    highlights: ['全球最大链上碳储备（1700万吨）', 'OHM Fork机制驱动碳价拉升', '质押KLIMA获取sKLIMA收益', 'AKlima/KLIMA/sKLIMA三层代币经济'],
  },
  {
    id: 41,
    name: 'Moss Earth MCO2 — 巴西亚马逊雨林碳信用',
    type: '碳信用', region: '巴西/拉美', platform: 'Ethereum', tvl: '900万吨', status: '运营中', track: '合规级', launchDate: '2020.04', issuer: 'Moss Earth', standard: 'ERC-20 MCO2',
    desc: '将巴西亚马逊雨林保护REDD+碳信用代币化，是拉丁美洲最大的碳信用代币化项目，与巴西政府合作，为雨林保护筹集资金，并完成空客、万事达等知名企业的碳中和认证。',
    highlights: ['巴西亚马逊雨林保护REDD+', '服务空客、万事达等企业碳中和', '链上即时退休证书', '与巴西政府合作'],
  },
  {
    id: 42,
    name: 'Regen Network REGEN — 生态系统碳信用',
    type: '碳信用', region: '全球', platform: 'Cosmos Regen Chain', tvl: '500万吨', status: '运营中', track: '创新级', launchDate: '2021.04', issuer: 'Regen Network', standard: 'Cosmos IBC原生',
    desc: '专为生态系统信用（碳汇、生物多样性、水质）设计的区块链，支持多种自然资本信用的发行、注销和追踪，引入科学验证方法，是碳信用质量最严格的链上平台之一。',
    highlights: ['专为生态系统信用设计的专属区块链', '碳汇+生物多样性+水质多类信用', '科学委员会方法论审查', 'Cosmos IBC跨链互操作'],
  },
  {
    id: 43,
    name: 'Nori NRT — 美国农业土壤碳汇代币',
    type: '碳信用', region: '美国', platform: 'Ethereum', tvl: '300万吨', status: '运营中', track: '创新级', launchDate: '2020.06', issuer: 'Nori Inc.', standard: 'ERC-20 NRT',
    desc: '专注美国农业土壤碳汇代币化，采用10年碳封存承诺+保险储备金机制，1 NRT代表1吨净负排放（土壤碳封存），质量远高于传统森林REDD+信用，吸引微软等企业购买。',
    highlights: ['土壤碳汇代币（质量高于REDD+）', '10年碳封存承诺+保险储备金', '微软采购碳信用合作方', '农民直接受益（无中间商抽佣）'],
  },
  {
    id: 44,
    name: 'C3 Protocol — 碳信用指数代币',
    type: '碳信用', region: '全球', platform: 'Polygon', tvl: '200万吨', status: '运营中', track: 'DeFi', launchDate: '2022.03', issuer: 'C3 Protocol', standard: 'ERC-20 UBO/NBO',
    desc: 'Toucan Protocol的竞争产品，发行UBO（通用碳信用）和NBO（自然碳信用）两种池代币，接受Verra VCS的多种项目类型，通过质量筛选机制提升链上碳信用整体质量。',
    highlights: ['UBO/NBO双碳信用池设计', '质量门槛高于BCT', '与KlimaDAO生态整合', '碳信用期货衍生品基础'],
  },
  // ── 供应链信贷 ──────────────────────────────────────────────────────────────
  {
    id: 45,
    name: 'Credix Finance — 拉美信贷代币化',
    type: '供应链信贷', region: '拉美', platform: 'Solana', tvl: '$1.5亿', status: '运营中', track: 'DeFi', launchDate: '2022.01', issuer: 'Credix Finance', standard: 'Solana SPL Token',
    desc: '在Solana链上将巴西、墨西哥等拉美市场的金融科技信贷代币化，机构投资者（如Fasanara Capital）向新兴市场借款方提供融资，通过链上智能合约自动还款，年化收益15%+。',
    highlights: ['拉美金融科技信贷RWA先驱', 'Solana链低Gas高TPS优势', 'Fasanara Capital等机构投资者', '年化15%+高收益（高风险匹配）'],
  },
  {
    id: 46,
    name: 'Almavest — 非洲小微金融贷款RWA',
    type: '供应链信贷', region: '非洲', platform: 'Ethereum', tvl: '$8000万', status: '运营中', track: 'DeFi', launchDate: '2022.06', issuer: 'Almavest / Goldfinch', standard: 'ERC-20 FIDU',
    desc: '通过Goldfinch协议将非洲小微金融机构（MFI）贷款组合代币化，为肯尼亚、尼日利亚等非洲国家小微企业主提供贷款，投资者获得稳定的美元计价收益。',
    highlights: ['非洲金融普惠RWA', '肯尼亚/尼日利亚小微贷款', 'Goldfinch协议基础设施', '真实世界影响力+投资收益双重目标'],
  },
  {
    id: 47,
    name: 'OpenTrade — 机构贸易融资代币化',
    type: '供应链信贷', region: '全球', platform: 'Ethereum', tvl: '$2亿', status: '运营中', track: '机构级', launchDate: '2023.05', issuer: 'OpenTrade Finance', standard: 'ERC-4626',
    desc: '将银行级贸易融资资产（信用证、应收账款、供应链贷款）代币化，面向机构投资者，与银行和贸易融资机构合作，通过ERC-4626金库标准提供透明的链上收益分配。',
    highlights: ['银行级贸易融资资产代币化', '与主流银行合作保障资产质量', 'ERC-4626金库标准', '机构客户专属（最低$1M起投）'],
  },
  {
    id: 48,
    name: 'Polytrade — 贸易融资NFT',
    type: '供应链信贷', region: '全球/印度', platform: 'Polygon', tvl: '$1.2亿', status: '运营中', track: 'DeFi', launchDate: '2021.07', issuer: 'Polytrade Finance', standard: 'ERC-721 + ERC-20',
    desc: '将发票和贸易应收账款代币化为NFT，由Atradius等信用保险机构担保，投资者购买NFT获得发票到期收益，Polygon链低成本运营，主要服务印度和东南亚中小企业。',
    highlights: ['Atradius信用保险背书', '印度/东南亚中小企业服务', '发票NFT化+流动性池', 'DeFi与TradFi贸易融资融合'],
  },
  // ── 其他 ──────────────────────────────────────────────────────────────────
  {
    id: 49,
    name: 'StraitsX XSGD — 新加坡元稳定币',
    type: '其他', region: '新加坡', platform: 'Ethereum/Stellar', tvl: 'S$8000万', status: '运营中', track: '合规级', launchDate: '2020.08', issuer: 'Xfers / StraitsX', standard: 'ERC-20 + Stellar',
    desc: '由Xfers运营的新加坡元支持稳定币，获MAS主要支付机构牌照，可用于RWA结算基础设施，是东南亚RWA生态的关键法币桥接工具。',
    highlights: ['MAS主要支付机构牌照', '新加坡元1:1锚定', 'XSGD用于RWA产品结算', 'StraitsX与Grab等主流平台集成'],
  },
  {
    id: 50,
    name: 'Solv Protocol — 债券/票据SFT',
    type: '其他', region: '全球', platform: 'Ethereum/BSC', tvl: '$8亿', status: '运营中', track: 'DeFi', launchDate: '2021.09', issuer: 'Solv Protocol', standard: 'ERC-3525 SFT',
    desc: '全球最大的ERC-3525半同质化代币（SFT）应用平台，将复杂金融工具（归属计划、票据、债券、GP/LP份额）代币化，同一合约支持不同期限/利率的债券分类管理。',
    highlights: ['ERC-3525 SFT最大商业应用平台', '项目方归属计划代币化（锁仓期管理）', 'TVL $8亿，用户5万+', '已集成OpenSea等主流NFT市场'],
  },
  {
    id: 51,
    name: 'Flux Finance — RWA抵押稳定币借贷',
    type: '其他', region: '全球', platform: 'Ethereum', tvl: '$1.5亿', status: '运营中', track: 'DeFi', launchDate: '2023.01', issuer: 'Flux Finance / Ondo', standard: 'ERC-20 fToken',
    desc: 'Compound v2 Fork，专门允许持有RWA代币（如OUSG）作为抵押品借出USDC/USDT，解决了RWA代币持有者流动性需求，是DeFi借贷市场与RWA资产整合的创新模型。',
    highlights: ['OUSG/USDY等RWA资产作为抵押', 'Compound v2 Fork架构', '解决RWA代币流动性需求', '链上合规KYC检查与借贷整合'],
  },
  {
    id: 52,
    name: 'Spark Protocol — RWA支持借贷',
    type: '其他', region: '全球', platform: 'Ethereum', tvl: '$25亿', status: '运营中', track: 'DeFi', launchDate: '2023.05', issuer: 'Phoenix Labs / MakerDAO', standard: 'ERC-20 spToken',
    desc: 'MakerDAO官方推出的借贷协议，以DAI为核心，将RWA作为抵押品生成DAI，DAI储蓄利率（DSR）由底层RWA收益（美债等）支撑，是DeFi-RWA深度融合的最大实验。',
    highlights: ['MakerDAO官方DeFi借贷前端', 'RWA资产支撑DAI稳定性', 'DAI储蓄利率DSR（由美债RWA收益驱动）', 'TVL $25亿，DeFi生态核心'],
  },
  // ── 中国/香港特色案例 ──────────────────────────────────────────────────────
  {
    id: 53,
    name: '蚂蚁链 — 供应链金融数字化',
    type: '供应链信贷', region: '中国大陆', platform: '蚂蚁链', tvl: '5000亿元+', status: '运营中', track: '联盟链级', launchDate: '2019.01', issuer: '蚂蚁集团', standard: '联盟链（无代币）',
    desc: '蚂蚁链将应收账款确权上链存证，供应商可凭链上数据向银行申请融资，已服务数万家中小企业，是中国境内最大的供应链金融区块链平台，完全不发行虚拟货币，符合境内监管要求。',
    highlights: ['中国境内最大供应链区块链平台', '应收账款确权不可篡改存证', '已连接400+银行金融机构', '5000亿元+交易量（不含代币）'],
  },
  {
    id: 54,
    name: '中银国际BOCI — 数字绿色结构票据',
    type: '债券', region: '香港', platform: 'Ethereum', tvl: 'HK$2.28亿', status: '已发行', track: '首例', launchDate: '2023.07', issuer: '中国银行（香港）/ 中银国际', standard: 'ERC-1400',
    desc: '中资背景银行在以太坊主网发行港元结构性票据全球首例，以太坊主网实现KYC合规+链上结算，引发业界对中资机构拥抱公链的广泛关注。',
    highlights: ['中资机构以太坊公链发行票据首例', '2.28亿港元规模', '瑞银全球托管', '推动香港中资机构RWA发展'],
  },
  {
    id: 55,
    name: '兴业银行 — 绿色债券供应链融资',
    type: '债券', region: '中国大陆', platform: 'FISCO BCOS', tvl: '100亿元', status: '运营中', track: '联盟链级', launchDate: '2021.03', issuer: '兴业银行', standard: 'FISCO BCOS（无代币）',
    desc: '兴业银行通过微众银行FISCO BCOS联盟链将绿色债券存证和供应链融资整合，实现绿色资金流向透明追踪，是中国ESG+区块链结合的代表案例。',
    highlights: ['中国首批绿色债券区块链存证', 'ESG资金流向链上追踪', 'FISCO BCOS联盟链合规', '监管机构认可绿债真实性验证'],
  },
  {
    id: 56,
    name: 'HashKey Capital — RWA基金代币化',
    type: '股权', region: '香港', platform: 'Ethereum', tvl: '$3亿', status: '运营中', track: '机构级', launchDate: '2023.09', issuer: 'HashKey Group', standard: 'ERC-3643',
    desc: 'HashKey Group旗下资产管理部门将加密资产+RWA混合基金份额代币化，面向香港专业投资者，是香港最具影响力的加密资管机构向RWA市场的战略转型。',
    highlights: ['香港持牌加密资管机构', '加密+RWA混合基金', 'SFC持牌框架合规', '机构投资者（$100万港元起投）'],
  },
  {
    id: 57,
    name: 'GreenX Exchange — 香港绿色债券代币平台',
    type: '债券', region: '香港', platform: 'Ethereum', tvl: '$1.5亿', status: '运营中', track: '合规级', launchDate: '2023.11', issuer: 'GreenX Exchange', standard: 'ERC-1400',
    desc: '香港专注绿色债券和ESG资产代币化的交易平台，将企业绿色债券和主权绿色债券代币化，提供二级市场流动性，是香港ESG+RWA融合发展的新兴力量。',
    highlights: ['专注绿色债券和ESG代币化', '香港持牌平台', '企业绿债+主权绿债双轨', 'ESG评分链上存证'],
  },
  {
    id: 58,
    name: 'OSL Digital — 香港首家上市加密金融',
    type: '其他', region: '香港', platform: 'Ethereum', tvl: '$5亿+', status: '运营中', track: '合规级', launchDate: '2020.12', issuer: 'OSL Digital Securities', standard: 'ERC-1400 + 私有链',
    desc: 'BC Group旗下，香港首家受SFC持牌且在香港联交所上市的加密金融机构，提供证券型代币承销、托管、RWA二级市场交易，是香港合规RWA生态的核心基础设施之一。',
    highlights: ['香港SFC持牌VATP首批获批机构', '在港交所主板上市（863.HK）', 'RWA证券型代币承销+托管', '全球首家上市加密金融合规平台'],
  },
  {
    id: 59,
    name: 'Matrixport RWA — 机构级结构化产品',
    type: '其他', region: '香港/新加坡', platform: 'Ethereum', tvl: '$10亿', status: '运营中', track: '机构级', launchDate: '2022.06', issuer: 'Matrixport', standard: 'ERC-20 + 结构化包装',
    desc: '比特大陆联合创始人创立的加密金融服务平台，将美债、信用票据等传统金融产品结合DeFi收益策略代币化，面向机构和高净值客户，提供超$10亿规模的结构化产品。',
    highlights: ['吴忌寒（比特大陆联创）创立', '美债+DeFi混合收益结构化产品', '$10亿机构管理资产规模', '香港/新加坡双监管合规'],
  },
  {
    id: 60,
    name: 'Amber Group RWA — 量化驱动RWA服务',
    type: '其他', region: '香港/新加坡', platform: 'Ethereum/Polygon', tvl: '$5亿', status: '运营中', track: '机构级', launchDate: '2023.01', issuer: 'Amber Group', standard: 'ERC-3643',
    desc: '领先的数字资产金融服务机构，将量化交易策略与RWA（国债、货币市场）组合成结构化产品，为机构客户提供链上流动性管理和RWA收益增强策略。',
    highlights: ['量化策略+RWA混合产品', '链上流动性管理创新', '机构客户$500万起投', '香港/新加坡双牌照合规'],
  },
  // ── 国际创新案例 ──────────────────────────────────────────────────────────
  {
    id: 61,
    name: 'JP Morgan Onyx — 债券回购代币化',
    type: '债券', region: '美国', platform: 'Onyx (Quorum)', tvl: '$3500亿', status: '运营中', track: '机构级', launchDate: '2020.11', issuer: 'JPMorgan Chase', standard: '私有Quorum链',
    desc: 'JPMorgan Onyx平台将机构间回购协议（Repo）代币化，实现日间流动性管理，已累计处理超3500亿美元交易量，是全球规模最大的机构级区块链金融平台。',
    highlights: ['全球最大机构级区块链金融平台', '$3500亿+累计交易量', '日间流动性管理（日内回购）', '黑石/巴克莱等机构合作'],
  },
  {
    id: 62,
    name: 'DBS Bank — 数字债券和代币化存款',
    type: '债券', region: '新加坡', platform: 'DBS DBDO / Ethereum', tvl: 'S$5亿', status: '运营中', track: '银行级', launchDate: '2021.06', issuer: '星展银行 DBS', standard: '私有链+ERC-20',
    desc: '新加坡最大银行，通过DBS Digital Exchange（DDEx）发行数字债券和代币化存款，参与MAS Project Guardian沙盒测试，是东南亚最具影响力的银行级RWA案例。',
    highlights: ['东南亚最大银行级RWA实践', 'MAS Project Guardian核心参与者', '代币化存款（Tokenized Deposit）测试', '星展数字资产交易所DDEx支撑'],
  },
  {
    id: 63,
    name: 'Société Générale FORGE SG — 欧洲债券STO先驱',
    type: '债券', region: '欧盟', platform: 'Ethereum / Tezos', tvl: '€3亿', status: '运营中', track: '机构级', launchDate: '2019.04', issuer: 'SG FORGE (法兴银行子公司)', standard: 'ERC-1400',
    desc: '法国兴业银行的数字资产子公司，是欧洲机构级STO的先驱，已多次在以太坊主网发行覆盖债券结构，并向法国金融市场监管局（AMF）申领PSAN牌照。',
    highlights: ['欧洲机构STO先驱（2019年）', 'AMF法国监管牌照PSAN', 'MakerDAO购入作为DAI抵押', '建立开放银行和DeFi桥梁'],
  },
  {
    id: 64,
    name: 'ANZ A$DC — 澳元稳定币与RWA结算',
    type: '其他', region: '澳大利亚', platform: 'Ethereum', tvl: 'A$6500万', status: '试点', track: '银行级', launchDate: '2022.03', issuer: '澳新银行 ANZ', standard: 'ERC-20',
    desc: '澳新银行发行A$DC（澳元稳定币），全球首次由授权存款机构（ADI）在以太坊公链铸造澳元稳定币，用于RWA碳信用购买和清算，开创银行+公链结算新范式。',
    highlights: ['全球首家ADI银行以太坊公链稳定币', '用于真实碳信用RWA交易清算', 'APRA监管合规', '与Victor Smorgon Group碳信用购买测试'],
  },
  {
    id: 65,
    name: 'Republic of Slovenia — 主权债券代币化',
    type: '债券', region: '欧盟', platform: 'Ethereum', tvl: '€3000万', status: '已发行', track: '主权级', launchDate: '2024.03', issuer: '斯洛文尼亚共和国财政部', standard: 'ERC-20 + 合规层',
    desc: '斯洛文尼亚成为欧洲首个在以太坊公链上发行主权零售债券的EU成员国，散户投资者可通过Slovenian National Treasury Bonds App购买，最低100欧元。',
    highlights: ['欧盟首个以太坊公链主权零售债券', '最低€100起投，面向散户', '斯洛文尼亚国家财政部背书', '推动EU零售债券数字化先例'],
  },
  {
    id: 66,
    name: 'World Bank Bond-i — 首只区块链债券',
    type: '债券', region: '全球', platform: 'Private (Commonwealth Bank)', tvl: 'A$1.1亿', status: '已发行', track: '主权级', launchDate: '2018.08', issuer: '世界银行 / 澳联邦银行', standard: '私有Ethereum链',
    desc: '2018年全球首只完全在区块链上创建、分配、转让、管理的债券，2年期A$1.1亿，由澳大利亚联邦银行承销，开创债券全生命周期链上管理先河。',
    highlights: ['全球首只全流程链上债券（2018年）', '世界银行主权信用背书', '澳大利亚联邦银行承销', '开创债券RWA历史先河'],
  },
  {
    id: 67,
    name: 'TrueFi TRU — 无抵押信用评分链上借贷',
    type: '供应链信贷', region: '全球', platform: 'Ethereum', tvl: '$1.5亿', status: '运营中', track: 'DeFi', launchDate: '2020.11', issuer: 'TrustToken Inc.', standard: 'ERC-20 tfToken',
    desc: 'TrueFi通过链上信用评分机制实现无抵押机构借贷，SAFU资金池提供安全保障，Alameda Research、Wintermute等曾是主要借款方，2022年FTX危机触发违约后全面改革信用审核机制。',
    highlights: ['链上信用评分无抵押借贷先驱', 'SAFU保险机制设计', '2022年FTX违约后重建（风控升级）', '转型聚焦真实世界机构信贷'],
  },
  {
    id: 68,
    name: 'Frax Finance sfrxETH — LST嵌入RWA结构',
    type: '其他', region: '全球', platform: 'Ethereum', tvl: '$10亿+', status: '运营中', track: 'DeFi', launchDate: '2022.10', issuer: 'Frax Finance', standard: 'ERC-4626',
    desc: 'Frax采用ERC-4626金库标准，将以太坊质押收益（LST）与国债RWA收益叠加，frxETH金库持有人同时获得以太坊质押收益+DeFi协议收益+部分RWA收益，是DeFi与RWA最复杂融合案例。',
    highlights: ['LST + RWA收益叠加创新模型', 'ERC-4626金库标准最大应用之一', 'Curve生态深度整合', 'TVL超$10亿'],
  },
  {
    id: 69,
    name: 'TokenSoft — 企业股权代币化平台',
    type: '股权', region: '美国', platform: 'Ethereum/Polygon', tvl: '$5亿', status: '运营中', track: '合规级', launchDate: '2018.01', issuer: 'TokenSoft Inc.', standard: 'ERC-1404 R-Token',
    desc: 'TokenSoft是美国合规证券代币发行平台，为科技公司、矿企、DeFi协议提供Reg D/S豁免的股权和债权代币化服务，ERC-1404标准的重要推动者。',
    highlights: ['ERC-1404标准主要推动者', '科技公司股权代币化专家', 'SEC注册Transfer Agent', '已服务60+项目代币发行'],
  },
  {
    id: 70,
    name: 'Republic — 早期股权众筹代币化',
    type: '股权', region: '美国', platform: 'Ethereum/Algorand', tvl: '$2.5亿', status: '运营中', track: '合规级', launchDate: '2020.11', issuer: 'Republic.co', standard: 'Republic Note ERC-20',
    desc: 'Republic在AngelList基础上将创业公司早期股权代币化，发行Republic Note代币，持有人可获得平台成功项目的超额回报分享权，将传统股权众筹接入区块链二级流通性。',
    highlights: ['创业公司早期股权代币化', 'Reg CF豁免面向非合格投资者', 'Republic Note持有超过10万用户', '对接DeFi二级市场流动性'],
  },
  {
    id: 71,
    name: 'Steakhouse Financial — DeFi国库RWA管理',
    type: '其他', region: '全球', platform: 'Ethereum', tvl: '$8亿', status: '运营中', track: 'DeFi', launchDate: '2023.01', issuer: 'Steakhouse Financial', standard: 'ERC-4626',
    desc: '为MakerDAO/Morpho等DeFi协议提供RWA国库管理咨询，将闲置稳定币配置到美债RWA，优化协议收益，是DeFi协议RWA资产配置的领先服务商，管理RWA资产超$8亿。',
    highlights: ['MakerDAO/Morpho等DeFi协议RWA顾问', '协议国库RWA配置优化', '管理$8亿+DeFi RWA资产', '开创DeFi协议主动RWA资产管理模式'],
  },
  {
    id: 72,
    name: 'Polymesh MERJ — 塞舌尔证券代币交易所',
    type: '股权', region: '塞舌尔/非洲', platform: 'Polymesh Chain', tvl: '$1亿', status: '运营中', track: '合规级', launchDate: '2022.06', issuer: 'MERJ Exchange / Polymesh', standard: 'Polymesh原生',
    desc: 'MERJ（Mahé金融市场）是全球首家在专业证券代币区块链（Polymesh）上运营的国家证券交易所，允许全球企业在塞舌尔上市证券型代币，开创新型国家证券交易所范式。',
    highlights: ['全球首家专业证券代币区块链国家交易所', '塞舌尔FSA监管合规', '全球企业均可申请上市', 'Polymesh原生合规基础设施'],
  },
  {
    id: 73,
    name: 'Obligate — 链上商业票据',
    type: '债券', region: '欧盟', platform: 'Polygon', tvl: '€3000万', status: '运营中', track: '中小企业级', launchDate: '2022.05', issuer: 'Obligate', standard: 'ERC-1400',
    desc: '专为欧洲中小企业发行链上商业票据（ECP）的平台，符合欧盟金融工具监管，将传统的商业票据融资搬上链，企业可绕过银行直接向投资者融资，成本降低约50%。',
    highlights: ['欧洲中小企业直接融资绕银行', '商业票据成本降低50%', 'BaFin德国监管合规', '日均发行量超€5000万'],
  },
  {
    id: 74,
    name: 'Fasanara Capital — 机构信贷基金代币化',
    type: '供应链信贷', region: '英国/欧盟', platform: 'Ethereum', tvl: '$6亿', status: '运营中', track: '机构级', launchDate: '2022.08', issuer: 'Fasanara Capital', standard: 'ERC-1400',
    desc: '英国顶级信贷对冲基金Fasanara Capital将其底层贷款资产代币化，机构投资者通过链上购买基金份额获得新兴市场信贷收益，是传统对冲基金RWA代币化的成熟案例。',
    highlights: ['英国顶级对冲基金RWA化', '新兴市场信贷组合代币化', 'FCA英国监管合规', '机构投资者$500万美元起投'],
  },
  {
    id: 75,
    name: 'Parcl Protocol — 房地产指数代币',
    type: '房地产', region: '美国', platform: 'Solana', tvl: '$1.2亿', status: '运营中', track: 'DeFi', launchDate: '2023.03', issuer: 'Parcl Labs', standard: 'Solana SPL',
    desc: '将美国主要城市（纽约、洛杉矶、迈阿密）的房地产价格指数代币化，投资者可做多/做空不同城市房市，无需实际购房即可获得房价涨跌敞口，是合成RWA的创新实践。',
    highlights: ['城市房价指数做多/做空', '无需实际购房的房产价格敞口', '纽约/洛杉矶/迈阿密等城市覆盖', 'Solana高速低成本支撑'],
  },
  {
    id: 76,
    name: 'ACM — 阿布扎比碳信用交易平台',
    type: '碳信用', region: '中东', platform: 'ADX Chain', tvl: '2000万吨', status: '运营中', track: '国家级', launchDate: '2023.01', issuer: 'ADX (Abu Dhabi Exchange)', standard: '私有许可链',
    desc: '阿布扎比交易所发起的中东地区最大碳信用代币化交易平台，将MENA区域碳项目（沙漠绿化、海洋碳汇）代币化，是中东碳市场数字化的核心基础设施。',
    highlights: ['MENA中东最大碳信用平台', '沙漠绿化+海洋碳汇代币化', '阿布扎比ADGM监管合规', 'COP28绿色金融峰会展示项目'],
  },
  {
    id: 77,
    name: 'Liqwid Finance — Cardano链RWA借贷',
    type: '其他', region: '全球', platform: 'Cardano', tvl: '$1亿', status: '运营中', track: 'DeFi', launchDate: '2022.04', issuer: 'Liqwid Finance', standard: 'Cardano Native Asset',
    desc: '在Cardano区块链上构建的借贷协议，允许用户抵押RWA相关资产获得ADA或稳定币贷款，是Cardano生态最大DeFi协议，探索非EVM链RWA应用可能性。',
    highlights: ['Cardano最大DeFi协议', '非EVM链RWA借贷探索', 'Plutus智能合约架构', 'EMURGO基金会支持'],
  },
  {
    id: 78,
    name: 'Compound Treasury — 机构美债收益',
    type: '债券', region: '美国', platform: 'Ethereum', tvl: '$5亿', status: '运营中', track: '机构级', launchDate: '2021.06', issuer: 'Compound Labs', standard: 'ERC-20 cToken',
    desc: 'Compound推出机构服务，将美债收益通过DeFi协议分发给机构投资者，是传统机构进入DeFi RWA生态的合规通道，采用KYB机制确保只有合规机构可参与。',
    highlights: ['DeFi原生协议机构化转型', '美债收益DeFi分发', 'KYB机构合规验证', '$500万美元最低投资门槛'],
  },
  {
    id: 79,
    name: 'Nexo — 加密+RWA混合收益产品',
    type: '其他', region: '全球', platform: 'Ethereum/BSC', tvl: '$30亿', status: '运营中', track: '零售级', launchDate: '2018.04', issuer: 'Nexo AG', standard: 'ERC-20 NEXO',
    desc: 'Nexo将加密借贷与RWA美债收益整合，向用户提供稳定的储蓄利率（部分由美债RWA支撑），是面向全球零售用户规模最大的加密+RWA产品，用户超400万。',
    highlights: ['400万+全球零售用户', '加密借贷+美债RWA利率叠加', '欧洲多国金融监管牌照', '$30亿管理资产'],
  },
  {
    id: 80,
    name: 'dAMM Finance — 机构流动性市场RWA',
    type: '供应链信贷', region: '全球', platform: 'Ethereum', tvl: '$1亿', status: '运营中', track: '机构级', launchDate: '2022.11', issuer: 'dAMM Finance', standard: 'ERC-20',
    desc: '专为大型加密交易机构（做市商、量化基金）提供短期无抵押循环信用额度的DeFi协议，底层信用评估由Credora机构信用评分系统支撑，是加密信用市场与RWA的创新融合。',
    highlights: ['做市商无抵押循环信贷', 'Credora机构信用评分', '专为加密原生机构设计', '日内流动性管理工具'],
  },
  {
    id: 81,
    name: 'Ark Invest — 公开市场证券代币化研究',
    type: '股权', region: '美国', platform: '研究阶段', tvl: '研究阶段', status: '规划中', track: '未来级', launchDate: '2024.06', issuer: 'ARK Invest', standard: '待定',
    desc: 'ARK Invest（木头姐）在其2024年报告中重点分析了上市公司股票代币化的可行性，预计2030年公开市场证券代币化将达到$5万亿规模，是业界最具影响力的RWA未来预测。',
    highlights: ['预测2030年公开市场RWA达$5万亿', '上市证券代币化路线图', 'ARK Invest品牌背书', '推动机构重视公开市场RWA'],
  },
  {
    id: 82,
    name: 'Tokeny T-REX Platform — ERC-3643合规基础设施',
    type: '其他', region: '卢森堡/全球', platform: 'Ethereum', tvl: '服务$50亿+资产', status: '运营中', track: '基础设施', launchDate: '2019.01', issuer: 'Tokeny Solutions', standard: 'ERC-3643 T-REX',
    desc: 'Tokeny是ERC-3643 T-REX协议的创建者，提供面向机构的合规RWA代币化基础设施，已服务100+全球机构发行合规证券代币，是MAS/SFC/AMF等主要监管机构认可的技术合规架构。',
    highlights: ['ERC-3643 T-REX协议原创开发者', '已服务100+全球机构发行', '被MAS/SFC/AMF认可的合规架构', 'OnchainID链上身份系统'],
  },
  {
    id: 83,
    name: 'Provenance Blockchain — 金融资产区块链',
    type: '其他', region: '美国', platform: 'Provenance Chain', tvl: '$100亿+', status: '运营中', track: '机构级', launchDate: '2018.06', issuer: 'Figure Technologies / Hash Inc', standard: 'Provenance原生',
    desc: 'Figure Technologies创建的专为金融资产代币化设计的Cosmos IBC兼容区块链，已处理超$100亿的HELOC（房产抵押贷款）、消费贷款、养老金账户代币化，是金融资产代币化最成熟的专用链之一。',
    highlights: ['$100亿+金融资产代币化', 'HELOC房产抵押贷款上链', 'Figure Technologies/$10亿估值', 'Cosmos IBC跨链兼容'],
  },
  {
    id: 84,
    name: 'Arca Labs — 美国国债NFT基金',
    type: '债券', region: '美国', platform: 'Ethereum', tvl: '$1.5亿', status: '运营中', track: '合规级', launchDate: '2020.07', issuer: 'Arca Asset Management', standard: 'ERC-1404 + SEC注册',
    desc: 'Arca发行ArCoin——首个向SEC完整注册的加密资产基金，底层持有美国国债，以ERC-1404代币形式发行，是SEC注册基金代币化的早期里程碑案例，为后来者提供监管路径参考。',
    highlights: ['SEC首批完整注册加密资产基金（2020年）', '底层持有美国国债', 'ERC-1404合规代币', '为BlackRock BUIDL等后来者铺路'],
  },
  {
    id: 85,
    name: 'REINNO — 欧洲商业地产代币化',
    type: '房地产', region: '欧盟', platform: 'Ethereum', tvl: '€1.5亿', status: '运营中', track: '中小企业级', launchDate: '2021.06', issuer: 'REINNO', standard: 'ERC-1400',
    desc: '捷克共和国为基地的欧洲商业地产代币化平台，将中欧（捷克、斯洛伐克、波兰）的商业地产代币化，符合欧盟MiFID II框架，最低1000欧元起投，专注开发中东欧地产代币化市场。',
    highlights: ['中欧地产代币化专注者', 'MiFID II合规框架', '最低€1000起投', '捷克/波兰/斯洛伐克资产组合'],
  },
  {
    id: 86,
    name: 'Figure Pay — SPDI银行代币化存款',
    type: '其他', region: '美国', platform: 'Provenance Chain', tvl: '$5亿', status: '运营中', track: '银行级', launchDate: '2022.03', issuer: 'Figure Technologies', standard: 'Provenance原生',
    desc: 'Figure Pay获得怀俄明州SPDI（特殊目的存款机构）银行牌照，将客户存款代币化为USDF（稳定币），实现24/7实时结算，是美国首个获得州银行牌照的稳定币/代币化存款产品。',
    highlights: ['美国首个州银行牌照代币化存款', '怀俄明州SPDI特殊牌照', 'USDF稳定币 1:1美元支持', '24/7实时结算（无需Fedwire）'],
  },
  {
    id: 87,
    name: 'Hamilton Lane SCOPE — 私募股权代币化',
    type: '股权', region: '美国/全球', platform: 'Polygon', tvl: '$10亿', status: '运营中', track: '机构级', launchDate: '2023.02', issuer: 'Hamilton Lane', standard: 'ERC-1400 + Securitize',
    desc: '全球顶级私募资产管理机构Hamilton Lane（管理资产8240亿美元）通过Securitize将SCOPE基金代币化，将机构级私募基金投资门槛从$500万降至$2万，Polygon链上发行，是最具影响力的顶级私募代币化案例。',
    highlights: ['管理$8240亿的顶级私募机构', '$500万门槛降至$2万（面向个人合格投资者）', 'Polygon链发行（低Gas成本）', 'Securitize合规基础设施支撑'],
  },
];
